import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from './queries';
import { Task, TaskTemplate } from 'types';

// Paginated response type from DRF
interface PaginatedResponse<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: T[];
}

// Backend wrapper response types
interface TemplateListResponse {
  success: boolean;
  templates: TaskTemplateBackend[];
}

interface TaskListResponse {
  success: boolean;
  tasks: TaskBackend[];
}

// Backend response types (snake_case)
interface TaskBackend {
  id: string;
  workspace_id: string;
  name: string;
  description?: string;
  recurrence: {
    type: string;
    dayOfWeek?: number;
    dayOfMonth?: number;
  };
  days_until_due?: number;
  created_at: string;
  updated_at: string;
}

interface TaskTemplateBackend {
  id: string;
  workspace_id: string;
  name: string;
  description?: string;
  tasks: TaskBackend[];
  task_count: number;
  created_at: string;
  updated_at: string;
}

// Transform functions (snake_case -> camelCase)
const transformTask = (task: TaskBackend): Task => ({
  id: task.id,
  workspaceId: task.workspace_id,
  name: task.name,
  description: task.description,
  recurrence: task.recurrence as Task['recurrence'],
  daysUntilDue: task.days_until_due,
  createdAt: task.created_at,
  updatedAt: task.updated_at,
});

const transformTemplate = (template: TaskTemplateBackend): TaskTemplate => ({
  id: template.id,
  workspaceId: template.workspace_id,
  name: template.name,
  description: template.description,
  tasks: (template.tasks || []).map(transformTask),
  createdAt: template.created_at,
  updatedAt: template.updated_at,
});

// Helper to safely extract results from various response formats
const extractResults = <T>(response: PaginatedResponse<T> | T[] | { templates?: T[]; tasks?: T[]; success?: boolean }): T[] => {
  if (Array.isArray(response)) {
    return response;
  }
  // Handle backend wrapper responses like { success: true, templates: [...] }
  if ('templates' in response && response.templates) {
    return response.templates;
  }
  if ('tasks' in response && response.tasks) {
    return response.tasks;
  }
  // Handle paginated response
  if ('results' in response) {
    return response.results || [];
  }
  return [];
};

export const templateApi = createApi({
  reducerPath: 'templateApi',
  baseQuery: axiosBaseQuery({
    baseUrl: import.meta.env.VITE_APP_BASE_URL as string,
  }),
  tagTypes: ['Template', 'Task'],
  endpoints: (builder) => ({
    // ============== Task Endpoints ==============
    getTasks: builder.query<Task[], { workspaceId: string }>({
      query: ({ workspaceId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/tasks/`, 
        method: 'get',
      }),
      transformResponse: (response: TaskListResponse | PaginatedResponse<TaskBackend> | TaskBackend[]) => 
        extractResults(response as any).map(transformTask),
      providesTags: ['Task'],
    }),
    getTask: builder.query<Task, { workspaceId: string; taskId: string }>({
      query: ({ workspaceId, taskId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/tasks/${taskId}/`, 
        method: 'get' 
      }),
      transformResponse: (response: { success: boolean; task: TaskBackend } | TaskBackend) => {
        const task = 'task' in response ? response.task : response;
        return transformTask(task);
      },
      providesTags: ['Task'],
    }),
    createTask: builder.mutation<Task, { workspaceId: string; data: Partial<Task> }>({
      query: ({ workspaceId, data }) => ({ 
        url: `/v1/workspaces/${workspaceId}/tasks/`, 
        method: 'post', 
        data 
      }),
      transformResponse: (response: { success: boolean; task: TaskBackend } | TaskBackend) => {
        const task = 'task' in response ? response.task : response;
        return transformTask(task);
      },
      invalidatesTags: ['Task'],
    }),
    updateTask: builder.mutation<Task, { workspaceId: string; taskId: string; data: Partial<Task> }>({
      query: ({ workspaceId, taskId, data }) => ({ 
        url: `/v1/workspaces/${workspaceId}/tasks/${taskId}/`, 
        method: 'patch', 
        data 
      }),
      transformResponse: (response: { success: boolean; task: TaskBackend } | TaskBackend) => {
        const task = 'task' in response ? response.task : response;
        return transformTask(task);
      },
      invalidatesTags: ['Task'],
    }),
    deleteTask: builder.mutation<void, { workspaceId: string; taskId: string }>({
      query: ({ workspaceId, taskId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/tasks/${taskId}/`, 
        method: 'delete' 
      }),
      invalidatesTags: ['Task'],
    }),

    // ============== TaskTemplate Endpoints ==============
    getTemplates: builder.query<TaskTemplate[], { workspaceId: string }>({
      query: ({ workspaceId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/task-templates/`, 
        method: 'get',
      }),
      transformResponse: (response: TemplateListResponse | PaginatedResponse<TaskTemplateBackend> | TaskTemplateBackend[]) => 
        extractResults(response as any).map(transformTemplate),
      providesTags: ['Template'],
    }),
    getTemplate: builder.query<TaskTemplate, { workspaceId: string; templateId: string }>({
      query: ({ workspaceId, templateId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/task-templates/${templateId}/`, 
        method: 'get' 
      }),
      transformResponse: (response: { success: boolean; template: TaskTemplateBackend } | TaskTemplateBackend) => {
        const template = 'template' in response ? response.template : response;
        return transformTemplate(template);
      },
      providesTags: ['Template'],
    }),
    createTemplate: builder.mutation<TaskTemplate, { workspaceId: string; data: Partial<TaskTemplate> & { taskIds?: string[]; tasks?: any[] } }>({
      query: ({ workspaceId, data }) => ({ 
        url: `/v1/workspaces/${workspaceId}/task-templates/`, 
        method: 'post', 
        data 
      }),
      transformResponse: (response: { success: boolean; template: TaskTemplateBackend } | TaskTemplateBackend) => {
        const template = 'template' in response ? response.template : response;
        return transformTemplate(template);
      },
      invalidatesTags: ['Template'],
    }),
    updateTemplate: builder.mutation<TaskTemplate, { workspaceId: string; templateId: string; data: Partial<TaskTemplate> & { task_ids?: string[]; tasks?: any[] } }>({
      query: ({ workspaceId, templateId, data }) => ({ 
        url: `/v1/workspaces/${workspaceId}/task-templates/${templateId}/`, 
        method: 'patch', 
        data 
      }),
      transformResponse: (response: { success: boolean; template: TaskTemplateBackend } | TaskTemplateBackend) => {
        const template = 'template' in response ? response.template : response;
        return transformTemplate(template);
      },
      invalidatesTags: ['Template'],
    }),
    deleteTemplate: builder.mutation<void, { workspaceId: string; templateId: string }>({
      query: ({ workspaceId, templateId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/task-templates/${templateId}/`, 
        method: 'delete' 
      }),
      invalidatesTags: ['Template'],
    }),
    addTaskToTemplate: builder.mutation<TaskTemplate, { workspaceId: string; templateId: string; taskId: string }>({
      query: ({ workspaceId, templateId, taskId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/task-templates/${templateId}/tasks/`, 
        method: 'post', 
        data: { task_id: taskId }
      }),
      transformResponse: (response: { success: boolean; template: TaskTemplateBackend } | TaskTemplateBackend) => {
        const template = 'template' in response ? response.template : response;
        return transformTemplate(template);
      },
      invalidatesTags: ['Template'],
    }),
    removeTaskFromTemplate: builder.mutation<void, { workspaceId: string; templateId: string; taskId: string }>({
      query: ({ workspaceId, templateId, taskId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/task-templates/${templateId}/tasks/${taskId}/`, 
        method: 'delete' 
      }),
      invalidatesTags: ['Template'],
    }),
    reorderTemplateTasks: builder.mutation<TaskTemplate, { workspaceId: string; templateId: string; taskIds: string[] }>({
      query: ({ workspaceId, templateId, taskIds }) => ({ 
        url: `/v1/workspaces/${workspaceId}/task-templates/${templateId}/reorder/`, 
        method: 'put', 
        data: { task_ids: taskIds }
      }),
      transformResponse: (response: { success: boolean; template: TaskTemplateBackend } | TaskTemplateBackend) => {
        const template = 'template' in response ? response.template : response;
        return transformTemplate(template);
      },
      invalidatesTags: ['Template'],
    }),
  }),
});

export const {
  // Task hooks
  useGetTasksQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  // Template hooks
  useGetTemplatesQuery,
  useGetTemplateQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useAddTaskToTemplateMutation,
  useRemoveTaskFromTemplateMutation,
  useReorderTemplateTasksMutation,
} = templateApi;

